import BottomSheet from '@components/BottomSheet'
import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { useBackAfterSheet } from '@hooks/useBackAfterSheet'
import { EarnWithdrawReview } from '@modules/Earn/Components/EarnWithdrawReview'
import { InfoRow } from '@modules/Earn/Components/shared'
import { useEarnWithdraw } from '@plug-core/hooks/earn/useEarnWithdraw'
import { EarnPosition, EarnVault } from '@plug-core/interfaces/earn'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { titleize } from '@plug-core/utils/titleize'
import { sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import { Button, Field, Input, ListGroup, Text } from '@ui'
import React, { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

interface ContentProps {
  vault: EarnVault
  position: EarnPosition
}

const Pill = ({ children, onClick }: { children: ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="cursor-pointer rounded-xs border-0 bg-chip px-2 py-1 font-text text-[12px] font-semibold text-fg transition-transform duration-150 select-none active:scale-95 motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
  >
    {children}
  </button>
)

const padToMinTwoDecimals = (s: string): string => {
  if (!s) return ''
  const [int, dec = ''] = s.split('.')
  return `${int}.${dec.padEnd(2, '0')}`
}

const EarnWithdrawContent: FC<ContentProps> = ({ vault, position }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [reviewVisible, setReviewVisible] = useState(false)
  const [errorPromptVisible, setErrorPromptVisible] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)

  const {
    amount,
    setAmount,
    balanceUsd,
    receiveNative,
    assetSymbol,
    error,
    withdrawState,
    executeWithdraw,
  } = useEarnWithdraw(position)

  const displayValue =
    amount === '' ? '' : `$${inputFocused ? amount : padToMinTwoDecimals(amount)}`

  const backAfterSheet = useBackAfterSheet()

  useEffect(() => {
    if (withdrawState === 'error') {
      setReviewVisible(false)
      setErrorPromptVisible(true)
    } else if (withdrawState === 'success') {
      setReviewVisible(false)
      backAfterSheet(t('earn.withdraw.completed'))
    }
  }, [withdrawState])

  const handleReviewClose = () => setReviewVisible(false)

  return (
    <div className="flex flex-col gap-4">
      <Titlebar onActionClick={() => navigate(-1)} title={t('earn.withdraw.title')} />

      <div className="mt-4 flex flex-col items-center gap-2">
        <Field className="w-full">
          <Input
            value={displayValue}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            onChange={(e) => sanitizeDecimalInput(e.target.value.replace(/\$/g, ''), setAmount)}
            placeholder="$0.00"
            inputMode="decimal"
            size="lg"
            bare
            className="tabular text-center font-display text-[32px] font-semibold"
          />
        </Field>

        <Text variant="caption" tone="fg-2" tabular>
          {t('earn.withdraw.available', { amount: formatMoney(balanceUsd) })}
        </Text>

        <div className="flex gap-2">
          <Pill onClick={() => setAmount(String(balanceUsd / 2))}>50%</Pill>
          <Pill onClick={() => setAmount(String(balanceUsd))}>{t('common.max')}</Pill>
        </div>
      </div>

      <ListGroup>
        <InfoRow label={t('earn.deposit.vault')} value={vault.name} />
        <InfoRow label={t('earn.protocol')} value={titleize(vault.protocol.name)} />
        <InfoRow
          label={t('earn.withdraw.youReceive')}
          value={formatNumber(receiveNative, assetSymbol)}
        />
      </ListGroup>

      <Button
        variant="primary"
        fullWidth
        disabled={!!error || withdrawState === 'loading'}
        onClick={() => setReviewVisible(true)}
      >
        {error || t('earn.withdraw.withdraw')}
      </Button>

      {/* Kept mounted so it can slide out. */}
      <BottomSheet
        visible={reviewVisible}
        onBackdropClick={() => withdrawState !== 'loading' && handleReviewClose()}
      >
        <EarnWithdrawReview
          vault={vault}
          amount={amount}
          receiveNative={receiveNative}
          assetSymbol={assetSymbol}
          withdrawState={withdrawState}
          onConfirm={executeWithdraw}
          onClose={handleReviewClose}
        />
      </BottomSheet>

      <Prompt show={errorPromptVisible} close={() => setErrorPromptVisible(false)}>
        <Text align="center" tone="fg-2">
          {t('earn.withdraw.withdrawFailed')}
        </Text>
        <Button variant="secondary" onClick={() => setErrorPromptVisible(false)}>
          {t('common.understood')}
        </Button>
      </Prompt>
    </div>
  )
}

export const EarnWithdraw = () => {
  const navigate = useNavigate()
  const { state } = useLocation() as {
    state: { vault: EarnVault; position: EarnPosition } | null
  }

  useEffect(() => {
    if (!state?.vault || !state?.position) {
      navigate(-1)
    }
  }, [])

  if (!state?.vault || !state?.position) {
    return null
  } else {
    return (
      <div className="mb-3 flex flex-col">
        <EarnWithdrawContent vault={state.vault} position={state.position} />
      </div>
    )
  }
}

export default EarnWithdraw
